import { UploaderFileTypes, ImageValidation, InvalidFileError } from '../../types/FileUpload';
import { validateImageSize } from './validateImageSize';

export interface ValidateFilesProps {
  /**
   * Files to validate
   */
  files: File[];
  /**
   * Accepted file types
   */
  accept?: UploaderFileTypes[];
  /**
   * Maximum file size in bytes
   */
  maxSize?: number;
  /**
   * maximum number of files
   */
  maxFiles: number;
  /**
   * Image validation
   */
  imageValidation?: ImageValidation;
  /**
   * Invalid files callback
   */
  onInvalidFilesDrop: (invalidFiles: { file: File; error: InvalidFileError }[]) => void;
  /**
   * Number of existing files
   */
  existingFilesCount: number;
}

/**
 * A function to validate files before uploading
 * It will return the files that are valid
 * It will also call the onInvalidFilesDrop callback with the files that are invalid
 * @param files - The files to validate
 * @param accept - The accepted file types
 * @param maxSize - The maximum file size
 * @param maxFiles - The maximum number of files
 * @param imageValidation - The validation rules for the image
 * @param onInvalidFile - The callback to call when a file is invalid
 * @returns The valid files
 */
export const validateFiles = async ({
  files,
  accept,
  maxSize,
  maxFiles,
  imageValidation,
  onInvalidFilesDrop,
  existingFilesCount,
}: ValidateFilesProps): Promise<File[]> => {
  // Check if too many files were provided
  const invalidFilesWithErrors: { file: File; error: InvalidFileError }[] = [];
  const validFiles: File[] = [];
  if (files.length > maxFiles) {
    files.slice(maxFiles).forEach((file) => {
      invalidFilesWithErrors.push({ file, error: { name: 'too-many-files', maxFiles } });
    });
    files = files.slice(0, maxFiles);
  }

  if (maxFiles > 1 && existingFilesCount > 0 && files.length + existingFilesCount > maxFiles) {
    files.slice(maxFiles - existingFilesCount).forEach((file) => {
      invalidFilesWithErrors.push({ file, error: { name: 'too-many-files', maxFiles } });
    });
    files = files.slice(0, maxFiles - existingFilesCount);
  }

  // Process each file
  await Promise.all(
    files.map(async (file) => {
      let isValid = true;

      // Check file type if accept is defined
      if (accept && accept.length > 0) {
        const fileExtension = `.${file.name.split('.').pop()?.toLowerCase()}`;
        const isAcceptedType = accept.some((type) => fileExtension === type);

        if (!isAcceptedType) {
          invalidFilesWithErrors.push({ file, error: { name: 'file-type-not-allowed' } });
          isValid = false;
        }
      }

      // Check file size if maxSize is defined
      if (isValid && maxSize !== undefined && file.size > maxSize) {
        invalidFilesWithErrors.push({ file, error: { name: 'file-too-large', maxSize } });
        isValid = false;
      }

      // Validate image dimensions if it's an image
      if (isValid && imageValidation && file.type.startsWith('image/')) {
        const invalidImageError = await validateImageSize({ file, imageValidation });
        if (invalidImageError) {
          invalidFilesWithErrors.push({ file, error: invalidImageError });
          isValid = false;
        }
      }

      if (isValid) {
        validFiles.push(file);
      }
    }),
  );
  if (invalidFilesWithErrors.length > 0) {
    onInvalidFilesDrop(invalidFilesWithErrors);
  }
  return validFiles;
};
